import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import RowLoader from "../UI/RowLoader";
import StarIcon from "../Svgs/StarIcon";
import CustomPagination from "../CustomPagination";
import { getCourseRatings } from "../../apis";
import { transformKeys } from "../../_helper";

const ReviewData = ({
  title,
  className = "tw-container tw-mx-auto tw-p-5 tw-bg-white tw-rounded-[10px] tw-shadow-md tw-relative",
}) => {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [isError, setIsError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", currentPage);

    navigate({ search: params.toString() });
  }, [navigate, currentPage]);

  useEffect(() => {
    setIsLoading(true);
    getCourseRatings(courseId, currentPage)
      .then((response) => {
        const handleData = response.data.data.map((d) => transformKeys(d));
        setData(handleData);
        setPaginator({
          last_page: response.data.last_page,
          current_page: response.data.current_page,
        });
      })
      .catch((error) => {
        console.error(error);
        setIsError(
          error.response.data.message || "error while fetching course ratings"
        );
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [courseId, currentPage]);

  const filterAndSortData = () => {
    return data;
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const hiddenColumns = ["course_id", "id", "partner_id"];
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating); // Get full stars
    const decimal = rating % 1; // Get decimal part

    const starsArray = [];

    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      starsArray.push(
        <StarIcon key={i} className="tw-w-5 tw-text-yellow-500" />
      );
    }

    // Add half-star or full-star based on decimal
    if (decimal > 0.5) {
      starsArray.push(<StarIcon className="tw-w-5 tw-text-yellow-500" />);
    } else if (decimal === 0.5) {
      starsArray.push(
        <span
          key="half"
          className="material-icons tw-text-[#FACC15] tw-text-[23px]"
        >
          star_half
        </span>
      );
    }

    return <div className="tw-flex tw-items-center">{starsArray}</div>;
  };

  const columns = useMemo(() => {
    return createColumns(data, [], hiddenColumns, true);
  }, [data]).map((col) => ({
    ...col,
    sortable: col.name === "Num",
    width: col.name === "Num" ? "70px" : "1fr",
    cell:
      col.name === "Rating"
        ? (row) => renderStars(row.rating)
        : col.name === "Partner Name"
        ? (row) => (
            <Link to={`/profile/${row.partner_id}`}>{row.partner_name}</Link>
          )
        : col.cell,
  }));

  return (
    <section className={className}>
      <h3 className="tw-text-xl tw-font-semibold tw-text-red-600 tw-pb-2 tw-mb-4 tw-border-b">
        {title}
      </h3>

      {isError ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-24 tw-w-full tw-text-center tw-text-red-600">
          {isError}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filterAndSortData()}
          customStyles={DATATABLE_CUSTOM_STYLES}
          progressPending={isLoading}
          progressComponent={<RowLoader />}
        />
      )}
      <div className="flex justify-center mt-4">
        <CustomPagination
          totalPages={paginator?.last_page}
          currentPage={paginator?.current_page || 1}
          onPageChange={handlePageChange}
        />
      </div>
    </section>
  );
};

export default ReviewData;
